import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useState, useEffect } from 'react';
import Navigation from "@/components/navigation";
import StarfieldBackground from "@/components/starfield-background";
import { Filter, Loader2, AlertCircle, Maximize2, Minimize2 } from "lucide-react";
import { apiService } from "@/lib/api";
// 4 streams max
const STREAM_COLORS = [
    '#0F766ECC', // teal
    '#7C3AEDCC', // purple
    '#B45309CC', // amber
    '#1D4ED8CC', // blue
];
const NO_STREAM_COLOR = '#374151CC';
const START_HOUR = 10;
const END_HOUR = 24;
const formatHour = (h) => {
    const adj = h % 24;
    if (adj === 0)
        return "12 AM";
    if (adj < 12)
        return `${adj} AM`;
    if (adj === 12)
        return "12 PM";
    return `${adj - 12} PM`;
};
export default function ScheduleByCommentator() {
    const [blocks, setBlocks] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [selectedDate, setSelectedDate] = useState("");
    const [fullscreen, setFullscreen] = useState(false);
    const [currentMinute, setCurrentMinute] = useState(() => {
        const now = new Date();
        return now.getHours() * 60 + now.getMinutes();
    });
    useEffect(() => {
        setLoading(true);
        apiService.getScheduleBlocks()
            .then((data) => {
            setBlocks(data);
            if (data.length > 0) {
                const earliest = data.reduce((min, b) => b.startTime < min ? b.startTime : min, data[0].startTime);
                setSelectedDate(earliest.slice(0, 10));
            }
        })
            .catch(() => setError("Failed to load schedule. Please try again."))
            .finally(() => setLoading(false));
    }, []);
    useEffect(() => {
        const tick = () => {
            const now = new Date();
            setCurrentMinute(now.getHours() * 60 + now.getMinutes());
        };
        const id = setInterval(tick, 60000);
        return () => clearInterval(id);
    }, []);
    const availableDates = Array.from(new Set(blocks.map(b => b.startTime.slice(0, 10)))).sort();
    const filteredBlocks = blocks.filter(b => b.startTime.startsWith(selectedDate));
    // Collect unique commentator names from the filtered blocks
    const commentatorSet = new Set();
    for (const b of filteredBlocks) {
        if (b.commentator1Name)
            commentatorSet.add(b.commentator1Name);
        if (b.commentator2Name)
            commentatorSet.add(b.commentator2Name);
    }
    const commentators = Array.from(commentatorSet).sort((a, b) => a.localeCompare(b));
    // Build stream → color map (sorted by streamId for consistency)
    const streamColorMap = new Map();
    const sortedStreams = Array.from(new Map(filteredBlocks
        .filter(b => b.streamId != null)
        .map(b => [b.streamId, b.streamName ?? `Stream ${b.streamId}`])).entries()).sort(([a], [b]) => a - b);
    sortedStreams.forEach(([id, name], idx) => {
        streamColorMap.set(id, { name, color: STREAM_COLORS[idx % STREAM_COLORS.length] });
    });
    const getBlockColor = (block) => block.streamId != null ? (streamColorMap.get(block.streamId)?.color ?? NO_STREAM_COLOR) : NO_STREAM_COLOR;
    const getRelativeHour = (timeStr) => {
        const base = new Date(selectedDate + 'T00:00:00');
        const time = new Date(timeStr);
        return Math.round((time.getTime() - base.getTime()) / (1000 * 60 * 60));
    };
    const timeSlots = Array.from({ length: END_HOUR - START_HOUR }, (_, i) => i + START_HOUR);
    const buildRow = (name) => {
        const skipIndices = new Set();
        return timeSlots.map((hour, i) => {
            if (skipIndices.has(i))
                return { kind: 'skip' };
            const block = filteredBlocks.find(b => {
                if (b.commentator1Name !== name && b.commentator2Name !== name)
                    return false;
                return getRelativeHour(b.startTime) === hour;
            });
            if (block) {
                const colSpan = Math.max(1, getRelativeHour(block.endTime) - hour);
                for (let j = i + 1; j < i + colSpan; j++)
                    skipIndices.add(j);
                return { kind: 'block', block, colSpan };
            }
            return { kind: 'empty' };
        });
    };
    const currentHour = Math.floor(currentMinute / 60);
    const isCurrentHour = (hour) => hour === currentHour;
    const dateSelector = (_jsxs("div", { className: "flex items-center gap-2", children: [_jsx(Filter, { className: "w-4 h-4 text-gray-400" }), _jsx("select", { value: selectedDate, onChange: e => setSelectedDate(e.target.value), disabled: loading || availableDates.length === 0, className: "px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white focus:outline-none focus:border-txs-red text-sm disabled:opacity-50", children: availableDates.map(d => (_jsx("option", { value: d, className: "bg-space-dark", children: new Date(d + 'T12:00:00').toLocaleDateString('en-US', {
                        weekday: 'long', month: 'long', day: 'numeric'
                    }) }, d))) }), _jsx("button", { onClick: () => setFullscreen(f => !f), title: fullscreen ? "Exit fullscreen" : "Fullscreen view", className: "p-2 rounded-lg bg-white/5 hover:bg-white/10 border border-white/10 text-gray-400 hover:text-white transition-colors", children: fullscreen ? _jsx(Minimize2, { className: "w-4 h-4" }) : _jsx(Maximize2, { className: "w-4 h-4" }) })] }));
    const scheduleTable = (_jsxs(_Fragment, { children: [loading && (_jsxs("div", { className: "flex items-center justify-center gap-3 py-24 text-gray-400", children: [_jsx(Loader2, { className: "w-6 h-6 animate-spin" }), _jsx("span", { className: "font-orbitron text-sm", children: "Loading schedule..." })] })), error && (_jsxs("div", { className: "flex items-center gap-3 p-6 rounded-xl border border-red-500/30 bg-red-500/10 text-red-400", children: [_jsx(AlertCircle, { className: "w-5 h-5 flex-shrink-0" }), _jsx("span", { children: error })] })), !loading && !error && commentators.length === 0 && (_jsx("div", { className: "text-center py-24 text-gray-500", children: _jsx("p", { className: "font-orbitron text-sm", children: "No commentators found for this date." }) })), !loading && !error && commentators.length > 0 && streamColorMap.size > 0 && (_jsx("div", { className: "flex flex-wrap gap-3 mb-3", children: Array.from(streamColorMap.entries()).map(([id, { name, color }]) => (_jsxs("div", { className: "flex items-center gap-2", children: [_jsx("span", { className: "inline-block w-4 h-4 rounded-sm flex-shrink-0", style: { backgroundColor: color } }), _jsx("span", { className: "text-sm text-gray-300 font-exo", children: name })] }, id))) })), !loading && !error && commentators.length > 0 && (_jsx("div", { className: fullscreen ? "flex-1 overflow-auto" : "overflow-auto rounded-xl border border-white/10", children: _jsxs("table", { className: "border-collapse text-sm", style: { tableLayout: 'fixed', width: '100%' }, children: [_jsx("thead", { children: _jsxs("tr", { children: [_jsx("th", { className: "border border-gray-600 p-3 bg-gray-800 font-orbitron font-bold text-center sticky left-0 top-0 z-30 text-xs", style: { minWidth: 140 }, children: selectedDate
                                            ? new Date(selectedDate + 'T12:00:00').toLocaleDateString('en-US', {
                                                weekday: 'long', month: 'numeric', day: 'numeric'
                                            }).toUpperCase()
                                            : '' }), timeSlots.map(hour => (_jsx("th", { className: "border border-gray-600 p-2 font-orbitron font-bold text-center sticky top-0 z-20 text-xs", style: {
                                            minWidth: 70,
                                            backgroundColor: isCurrentHour(hour) ? '#EF444466' : '#1F2937',
                                            outline: isCurrentHour(hour) ? '2px solid #EF4444' : undefined,
                                        }, children: formatHour(hour) }, hour)))] }) }), _jsx("tbody", { children: commentators.map(name => {
                                const row = buildRow(name);
                                return (_jsxs("tr", { children: [_jsx("td", { className: "border border-gray-700 px-3 py-2 font-semibold text-white bg-gray-900 sticky left-0 z-10 text-sm", style: { minWidth: 140 }, children: name }), row.map((cell, i) => {
                                            if (cell.kind === 'skip')
                                                return null;
                                            const hour = timeSlots[i];
                                            const isCurrent = isCurrentHour(hour);
                                            if (cell.kind === 'empty') {
                                                return (_jsx("td", { className: "border border-gray-700/60", style: {
                                                        minWidth: 70,
                                                        backgroundColor: isCurrent ? '#EF444411' : undefined,
                                                    } }, i));
                                            }
                                            return (_jsx("td", { colSpan: cell.colSpan, className: "border border-gray-600 px-2 py-2 text-center align-middle font-bold text-white text-sm", style: {
                                                    backgroundColor: getBlockColor(cell.block),
                                                    textShadow: '0 1px 3px rgba(0,0,0,0.8)',
                                                    minWidth: 70,
                                                }, children: cell.block.streamUrl ? (_jsx("a", { href: cell.block.streamUrl, target: "_blank", rel: "noopener noreferrer", className: "hover:underline text-white", children: cell.block.gameAbbreviation ?? '—' })) : (cell.block.gameAbbreviation ?? '—') }, i));
                                        })] }, name));
                            }) })] }) }))] }));
    if (fullscreen) {
        return (_jsxs("div", { className: "fixed inset-0 z-50 bg-space-dark text-white flex flex-col", children: [_jsxs("div", { className: "flex items-center justify-between px-4 py-2 border-b border-white/10 bg-gray-900/80 backdrop-blur-sm flex-shrink-0", children: [_jsx("span", { className: "font-orbitron font-bold text-txs-red text-lg", children: "Schedule by Commentator" }), dateSelector] }), scheduleTable] }));
    }
    return (_jsxs("div", { className: "min-h-screen bg-space-dark text-white relative", children: [_jsx(StarfieldBackground, {}), _jsx(Navigation, {}), _jsx("main", { className: "pt-20 pb-16 px-4 relative z-10", children: _jsxs("div", { className: "max-w-full mx-auto", children: [_jsxs("div", { className: "mb-6 flex items-center justify-between flex-wrap gap-4", children: [_jsx("h1", { className: "font-orbitron font-bold text-4xl text-txs-red", children: "Schedule by Commentator" }), dateSelector] }), scheduleTable] }) })] }));
}
